import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, state, style, transition, animate, query, stagger } from '@angular/animations';
import { HttpClient } from '@angular/common/http';
import { catchError, debounceTime, forkJoin, interval, Observable, of, Subject, Subscription, switchMap, tap, timer, BehaviorSubject, map, takeUntil, finalize, timeout, EMPTY, filter, take, distinctUntilChanged } from 'rxjs';
import { AuthService, CommentsService, DistributionGroupService, SubletService, SysNotifyService, TenantService, VendorsService, WebSocketService, TaskUpdatePayload, TempUserService, environment, GmAssignmentService, DashboardLiveSyncService, DASHBOARD_SOCKET_DEBOUNCE_MS, DASHBOARD_POLLING_WS_DISCONNECTED_MS, DashboardSocketHandler } from '@org/shared-services';
import { commentsCapture, Sublet, Task, TaskCounts, TempUser, User, Role, DistributionGroup, SubletVendorAssignment, BlueprintProgress, MediaItem } from '@org/interfaces';
import { Router, RouterModule } from '@angular/router';
import * as moment from 'moment';
import { RequestService, TaskService } from '@org/shared-services';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NotificationsComponent } from '../notification/notifications.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faCheck, faEye, faImage, faTrash } from '@fortawesome/free-solid-svg-icons';
import { NotificationsService, UserService } from '@org/shared-services';
import { MatDialog } from '@angular/material/dialog';
import {AssignToDropdownComponent, BlueprintProgressModalComponent, CommentThreadComponent, ConfirmationDialogComponent, ConfirmationModalTWComponent, DistributionGroupsManagerComponent, ModalService, VendorAssociateComponent } from '@org/components'
import { SubletSelectionComponent } from '../subletSelection/subletSelection.component';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import {IonicModule } from '@ionic/angular'
import { MatSnackBar } from '@angular/material/snack-bar';
import { notEmptyArrayValidator, VendorFilterPipe, TruncatePipe } from "@org/utilities";
import { CommentTooltipComponent } from './comment-tooltip/comment-tooltip.component';
import { HostListener } from '@angular/core';
import { TimeoutError } from 'rxjs';
import { DailyReportService } from '@org/shared-services';
import { ImageViewerService } from 'libs/shared-services/src/lib/image-viewer/image-viewer.service';
import { SoundService } from '@org/shared-services';


interface Group extends DistributionGroup {
    name: string;
    _id: string;
    isGlobal: boolean;
}

interface Request {
    _id: string;
    name: string;
}

interface InitialDataResponse {
    user: User;
    requests: Request[];
    employees: User[];
    taskCounts: TaskCounts;
    pendingVendors: TempUser[];
}

type InitialDataPayload = {
    [K in keyof InitialDataResponse]: Observable<InitialDataResponse[K]>;
};

@Component({
    selector: 'app-dashboard',
    imports: [FormsModule, ReactiveFormsModule, CommonModule, NotificationsComponent,
        FontAwesomeModule, ConfirmationDialogComponent, VendorAssociateComponent,
        SubletSelectionComponent, MatTooltip, IonicModule, AssignToDropdownComponent, 
        VendorFilterPipe, TruncatePipe, MatTooltipModule, 
        CommentTooltipComponent, CommentThreadComponent, RouterModule, ConfirmationModalTWComponent, DistributionGroupsManagerComponent],
    templateUrl: './dashboard.component.html',
    styleUrl: './dashboard.component.css',
    animations: [
        trigger('slideInOut', [
            state('in', style({
                transform: 'translateX(0)'
            })),
            transition(':enter', [
                style({ transform: 'translateX(100%)' }),
                animate('300ms cubic-bezier(0.4, 0, 0.2, 1)')
            ]),
            transition(':leave', [
                animate('300ms cubic-bezier(0.4, 0, 0.2, 1)', style({ transform: 'translateX(100%)' }))
            ])
        ]),
        trigger('numberChange', [
            transition(':increment', [
                style({ color: 'green', transform: 'scale(1.2)' }),
                animate('200ms cubic-bezier(0.4, 0, 0.2, 1)', style({ color: '*', transform: 'scale(1)' }))
            ]),
            transition(':decrement', [
                style({ color: 'red', transform: 'scale(1.2)' }),
                animate('200ms cubic-bezier(0.4, 0, 0.2, 1)', style({ color: '*', transform: 'scale(1)' }))
            ])
        ]),
        trigger('taskList', [
            transition('* => *', [
                query(':leave', [
                    style({ position: 'absolute', left: 0, right: 0, opacity: 1 })
                ], { optional: true }),
                query(':enter', [
                    style({
                        opacity: 0,
                        transform: 'translateY(8px)'
                    })
                ], { optional: true }),
                query(':leave', [
                    stagger(20, [
                        animate('150ms cubic-bezier(0.4, 0, 0.2, 1)', style({
                            opacity: 0,
                            transform: 'translateY(-8px)'
                        }))
                    ])
                ], { optional: true }),
                query(':enter', [
                    stagger(25, [
                        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)', style({
                            opacity: 1,
                            transform: 'translateY(0)'
                        }))
                    ])
                ], { optional: true })
            ])
        ]),
        trigger('fadeInOut', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate('150ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 1 }))
            ]),
            transition(':leave', [
                animate('150ms cubic-bezier(0.4, 0, 0.2, 1)', style({ opacity: 0 }))
            ])
        ]),
        trigger('buttonState', [
            state('idle', style({
                transform: 'scale(1)'
            })),
            state('loading', style({
                transform: 'scale(0.98)'
            })),
            transition('idle <=> loading', [
                animate('200ms cubic-bezier(0.4, 0, 0.2, 1)')
            ])
        ]),
        trigger('slideUpDown', [
            state('void', style({
                transform: 'translateY(100%)'
            })),
            state('*', style({
                transform: 'translateY(0)'
            })),
            transition('void <=> *', [
                animate('300ms cubic-bezier(0.4, 0, 0.2, 1)')
            ])
        ])
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    styles: [`
    ::ng-deep .multiline-tooltip {
      white-space: pre-line !important;
      max-width: 300px !important;
      font-size: 12px !important;
      padding: 8px !important;
      background: rgba(0, 0, 0, 0.9) !important;
    }
  `]
})
export class DashboardComponent implements OnInit, OnDestroy, AfterViewInit  {

  @ViewChild('techRequestsButton') techRequestsButton!: ElementRef<HTMLButtonElement>;
  @ViewChild('myTasksButton') myTasksButton!: ElementRef<HTMLButtonElement>;
  @ViewChild('myRequest') shopRequestButton!: ElementRef<HTMLButtonElement>;
  @ViewChild('techRequestsUnAssigned') techRequestsUnAssigned!: ElementRef<HTMLButtonElement>;
  @ViewChild(NotificationsComponent) notificationComponent!: NotificationsComponent;

  dropdownOpen = false;
  tasks$!: Observable<Task[]>;
  totalTasks$!: Observable<number>;
  totalTasksRead$!: Observable<TaskCounts>;
  userAssignedTo$!: Observable<boolean>;
  isUserAssigned:boolean = false
  metricsExpanded = false;
  updTask!: Task;
  user!: any;
  usr!: any;
  currentPage = 1;
  totalPages!: number;
  limit = 10; // Number of tasks per page
  timeUpdateSubscription!: Subscription;
  activeFilter = 'TR';
  isLoading = true;
  noData = false;
  
  task = {
    employee: '',
    request: '',
    comments: '',
    isUrgent: false,
    tenant: '',
    sublets: []
  };
  requests: any[] = [];
  tenantUsers: any[] = [];
  globalGroups: any[] = [];
  privateGroups: any[] = [];
  filteredGlobalGroups: any[] = [];
  filteredPrivateGroups: any[] = [];
  filteredUsers: any[] = [];
  selectedSubletOptions: any[] = []; // Array to store selected sublet options
  uploadedImages: File[] = [];
  slideOutVisible = false;
  slideOutVisible2 = false;
  slideOutVisible3 = false;
  slideOutVisible4 = false;
  isEditMode = false;

  // Flag display text mapping
  flagTextMap: { [key: string]: string } = {
    'red': '+30mins',
    'yellow': '+15mins',
    'green': 'Completed',
    'white': ''
  };
  requestName: any;
  faCheck = faCheck;
  faEye = faEye;
  faImage = faImage;
  faTrash = faTrash;
  viewUpdateSub!: Subscription;
  lastActiveButton: HTMLButtonElement | null = null;
  showSubletComponent = false;
  searchTerm: string = '';
  dropdownAssignedToOpen = false; // Flag to control dropdown state
  selectedOption: string = ''; // Holds the selected option text

  private searchSubject = new Subject<string>();
  private previousAssignee: string | null = null;

  newTechQueueCount: number = 0;  // Example, fetch this count from your service
  newTechRequestsCount: number = 0;  // Example, fetch this count from your service
  newIncomingTasksCount: number = 0;  // Example, fetch this count from your service
  newOutgoingTasksCount: number = 0;  // Example, fetch this count from your service
  allTechRequestsCount: number = 0;  // Example, fetch this count from your service
  allTechRequestsCount_cardCount: number = 0;  // Example, fetch this count from your service
  overDueTechRequestsCount: number = 0;  // Example, fetch this count from your service
  overDueTechRequestsByRoleCount: number = 0;  // Example, fetch this count from your service
  myTechRequests_cardCount: number = 0;  // Example, fetch this count from your service
  incoming_cardCount: number = 0;  // Example, fetch this count from your service
  outgoing_cardCount: number = 0;  // Example, fetch this count from your service
  csrOpenTasksCount: number = 0;  // Example, fetch this count from your service
  csrOpenTasksCount_cardCount: number = 0;  // Example, fetch this count from your service
  porterPending: number = 0;
  porterPending_cardCount: number = 0;

  isFocused: boolean = false;
  loadingComments: boolean = false;
  commentSectionVisible = false;

  // Modal visibility
  isInviteVendorModalVisible = false;

  // Reactive Form Group
  inviteVendorForm!: FormGroup;
  vendorWorkForm!: FormGroup;
  searchQuery: string = '';
  selectedVendors: any[] = []; // Array to hold selected vendors
  vendorRequestDetails: string = ''; // Holds the vendor request details
  vendorRequestRONumber: string = ''; // Holds the vendor request RO number
  selectedFiles: File[] = [];
  imagePreviews: string[] = []; // Array to hold image preview URLs
  vendors: any[] = []
  subletSelected!: SubletVendorAssignment;

  // Add new property for request type filter
  selectedRequestType: string = '';
  selectedRequestName: string = 'All Request Types';
  availableRequestTypes: any[] = [];
  requestTypeCounts: { [key: string]: number } = {};
  isFilterLoading: boolean = false;

  vendorCategories = [
    { value: 'Glass', label: 'Glass' },
    { value: 'Striping', label: 'Striping' },
    { value: 'Sensor', label: 'Sensor' },
    { value: 'Alignment', label: 'Alignment' },
    { value: 'Misc', label: 'Misc' }
  ];

  private excludedNames = environment.excludedNames;

  isGMView = false;
  private userCache: { [key: string]: any } = {};

  tooltipVisible = false;
  tooltipPosition = { x: 0, y: 0 };
  activeTooltipTask: Task | null = null;
  isTooltipHovered = false;

  tooltipTimer: any;
  tooltipCloseDelay = 1500; // 3 seconds

  taskMetrics = {
    active: 0,
    overdue: 0
  };

  lastUpdateTime: Date = new Date();
  private pollingTickInterval: ReturnType<typeof setInterval> | null = null;
  private isWebSocketConnected = false;
  private dashboardSocketHandlers: DashboardSocketHandler[] = [];
  private socketBindAttempts = { count: 0 };

  viewMode: 'list' | 'grid' = 'list';
  urgentFilter = false;
  unreadFilter = false;

  showKeyboardShortcutsModal = false;

  isNewUser: boolean = false;

  // Add new properties
  private taskCache: { [key: string]: { [page: number]: Task[] } } = {};
  private lastFetchTime: { [key: string]: { [page: number]: number } } = {};
  private readonly CACHE_DURATION = 30000; // 30 seconds cache

  // Add new property to track pagination loading state
  isPaginationLoading = false;

  // Add property to track direction of pagination
  private paginationDirection: 'forward' | 'backward' | null = null;

  // Add new property to track active requests
  private activeRequests: { [key: string]: Subscription } = {};

  private taskUpdateSubscription: Subscription | undefined;

  // Add properties for monthly closing goal
  monthlyBudget: number = 0;
  actualClosed: number = 0;
  remainingWorkDays: number = 0;
  pendingVendorApprovalsCount: number = 0;

  private longPressTimeout: any;
  private readonly LONG_PRESS_DURATION = 500; // 500ms for long press
  private isLongPress = false;

  private readonly destroy$ = new Subject<void>();

  // Add these properties to the class
  imageToDelete: any = null;
  showDeleteImageModal = false;
  showCompleteTaskModal = false;
  showDeleteTaskModal = false;
  showCloseCSRTaskModal = false;
  showClosePorterTaskModal = false;

  // Add properties to store previous counter values
  private prevAllTechRequestsCount: number = 0;
  private prevNewTechRequestsCount: number = 0;
  private prevIncomingTasksCount: number = 0;
  private prevMyTechRequestsCount: number = 0;
  private prevIncomingCardCount: number = 0;
  private prevOutgoingCardCount: number = 0;
  /** After first `getTaskCounts` snapshot, play chime when counters rise (avoids sound on initial load). */
  private hasEstablishedTaskCountBaseline = false;

  private techRequestCache: { [id: string]: any } = {};

  showDistroGroupTaskModal = false;
  pendingDistroTaskData: any = null;
  showDistributionGroupsManager = false;

  readonly distroMethodModalMessage =
    'Share with Group: one task for the team — when anyone completes it, it is done for everyone. ' +
    'Send Individually: each member gets their own task and must complete their own.';

  private currentTenant: any = null;

  getTenantContext(): string {
    return this.resolveDashboardTenant() ?? '';
  }

  private resolveDashboardTenant(): string | undefined {
    return this.liveSync.resolveDashboardTenantId(this.user, this.currentTenant);
  }

  openDistroGroupTaskModal(taskData: any) {
    this.pendingDistroTaskData = taskData;
    this.showDistroGroupTaskModal = true;
  }

  get updatedAgoLabel(): string {
    const seconds = Math.max(0, Math.round((Date.now() - this.lastUpdateTime.getTime()) / 1000));
    if (seconds < 60) return 'Updated just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `Updated ${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Updated ${hours}h ago`;
    return `Updated ${this.lastUpdateTime.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
  }

  get mobileFlowToggleLabel(): string {
    if (this.metricsExpanded) return 'Show less';
    const roles = this.user?.roles ?? [];
    if (roles.includes('csr') || roles.includes('porter')) return 'Show incoming';
    return 'Show incoming and outgoing';
  }

  canManageDistributionGroups(): boolean {
    const roles = this.user?.roles ?? [];
    const allowed = ['gm', 'estimator', 'csr', 'parts coordinator', 'porter', 'admin'];
    return roles.some((r: string) => allowed.includes(r));
  }

  openDistributionGroupsManager(): void {
    this.showDistributionGroupsManager = true;
    this.reloadDistributionGroupsFromManager();
    this.cdr.markForCheck();
  }

  reloadDistributionGroupsFromManager(): void {
    const tenantContext = this.getTenantContext();
    if (!tenantContext) return;
    this.distroGroupService.getDistributionGroupsByTenant(tenantContext).subscribe({
      next: (response) => {
        this.globalGroups = response.data.filter((group) => group.isGlobal);
        this.privateGroups = response.data.filter((group) => !group.isGlobal);
        this.filterOptions();
        this.cdr.markForCheck();
      },
    });
  }

  distroGroupTask(mode: 'shared' | 'individual') {
    if (this.pendingDistroTaskData) {
      this.pendingDistroTaskData.distributionMode = mode;
      if (this.isEditMode) {
        this.taskService.updateTask(
          <string>this.pendingDistroTaskData._id,
          this.pendingDistroTaskData,
          "Updating Task",
          "Updating Task"
        ).subscribe(() => {
          this.toggleSlideOut();
          this.updateView();
          this.pendingDistroTaskData.comments = '';
          this.cdr.markForCheck();
        });
      } else {
        this.taskService.createRequest(this.pendingDistroTaskData).subscribe({
          next: () => {
            this.applyAfterLocalCreate();
            this.toggleSlideOut();
            this.sysNotifyService.showSuccess('Request created successfully', 3000, 'right', 'top');
          },
          error: (error) => {
            console.error('Error creating request:', error);
            this.sysNotifyService.showSuccess('Error creating request', 3000, 'right', 'top');
          }
        });
      }
    }
    this.showDistroGroupTaskModal = false;
    this.pendingDistroTaskData = null;
  }

  constructor(private http: HttpClient,
    private authService: AuthService,
    private taskService: TaskService,
    private requestService: RequestService,
    private userService: UserService,
    private tenantService: TenantService,
    private notificationService: NotificationsService,
    public subletService: SubletService,
    private tempUserService: TempUserService,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private router: Router,
    private ws: WebSocketService,
    private distroGroupService: DistributionGroupService,
    private commentService: CommentsService,
    private sysNotifyService: SysNotifyService,
    private fb: FormBuilder,
    private vendorsService: VendorsService,
    private dailyReportService: DailyReportService,
    private imageViewerService: ImageViewerService,
    private modalService: ModalService,
    private soundService: SoundService,
    private gmAssignmentService: GmAssignmentService,
    private liveSync: DashboardLiveSyncService
    ) {
    this.tasks$ = this.taskService.tasks$;
    this.totalTasks$ = this.taskService.totalTasks$;
    this.totalTasksRead$ = this.taskService.totalTasksRead$;

    this.userAssignedTo$ = this.tenantService.userAssignedTo$;
    this.isUserAssigned = this.tenantService.isAssignedToEnabled();

     // Initialize the Invite Vendor Form
     this.inviteVendorForm = this.fb.group({
      servicesOffered: ['', Validators.required],
      contactName: ['', Validators.required],
      vendorName: [''],
      phoneNumber: ['', Validators.required],
      emailAddress: ['']
    });

    this.vendorWorkForm = this.fb.group({
      requestDetails: ['', Validators.required],
      roNumber: ['', Validators.required],
      selectedVendors: [this.selectedVendors, [Validators.required, notEmptyArrayValidator()]]
      
    });

    // Set initial values
    this.vendorWorkForm.patchValue({
      requestDetails: this.vendorRequestDetails,
      roNumber: this.vendorRequestRONumber,
      selectedVendors: this.selectedVendors
    });

  }

  ngOnInit(): void {
    this.user = this.authService.currentUserValue;
    if (this.user && this.user.roles.includes('gm')) {
      this.gmAssignmentService.getCurrentTenant().subscribe((res: any) => {
        this.currentTenant = res.data.tenant;
        this.reloadDistributionGroupsFromManager();
        this.cdr.markForCheck();
        this.initAfterTenant();
      });
    } else {
      this.currentTenant = this.user?.tenant;
      this.initAfterTenant();
    }
  }

  private initAfterTenant() {
    if (
      this.user &&
      (this.user.roles.includes('am') ||
       this.user.roles.includes('vendor') ||
       this.user.roles.includes('tech'))
    ) {
      return; // Prevent logic from running for AM, Vendor, and Tech roles
    }
    this.checkIfNewUser();
    // Subscribe to user observable and only proceed when user is available
    this.authService.currentUser
      .pipe(filter((user: any) => !!user && !!user._id))
      .subscribe((user: any) => {
        this.user = user;
        this.preloadTechRequests();
        // Connect to WebSocket and monitor connection status
        this.ws.connect();
        // Set initial active filter based on user role and permissions
        this.initializeActiveFilter();
        // Load all initial data
        this.loadInitialData();
        // Set up subscriptions with takeUntil
        this.setupSubscriptions();
        this.setupSocketRefreshDebounce();
        this.setupDashboardSocketRefresh();
        this.setupAdaptivePolling();
        this.ws.connectionStatus$.pipe(takeUntil(this.destroy$)).subscribe(isConnected => {
          this.isWebSocketConnected = isConnected;
          if (isConnected) {
            this.liveSync.unbindTaskRefreshSocket(this.ws, this.dashboardSocketHandlers);
            this.socketBindAttempts.count = 0;
            this.setupDashboardSocketRefresh();
          }
        });
        // Load monthly report for GM
        if (this.user?.roles.includes('gm')) {
          this.loadMonthlyReport();
        }
      });
    // Debounced filter change handler
    this.filterChangeSubject
      .pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateView();
      });
  }

  private preloadTechRequests() {
    this.requestService.getRequests('tech').subscribe((techRequests: any[]) => {
      techRequests.forEach(req => {
        if (req && req._id) {
          this.techRequestCache[req._id] = req;
        }
      });
    });
  }

  private initializeActiveFilter(): void {
    if (this.user?.roles.includes('gm')) {
      this.activeFilter = 'GMC';
      this.setActiveButtonWithDelay(this.techRequestsButton?.nativeElement, 'GMC');
    } else if (this.user?.roles.includes('csr')) {
      this.activeFilter = 'CSR';
      this.setActiveButtonWithDelay(this.techRequestsButton?.nativeElement, 'CSR');
    } else if (this.user?.roles.includes('porter')) {
      this.activeFilter = 'PR';
      this.setActiveButtonWithDelay(this.techRequestsButton?.nativeElement, 'PR');
    } else {
      this.activeFilter = 'TR';
      this.setActiveButtonWithDelay(this.techRequestsButton?.nativeElement, 'TR');
    }
  }

  private setActiveButtonWithDelay(button: HTMLButtonElement | undefined, tag: string): void {
    if (button) {
        setTimeout(() => {
            this.setActiveButton(button, tag);
            // Removed this.updateView() to prevent duplicate fetchTasks on load
        });
    }
  }

  private setupSubscriptions(): void {
    // Subscribe to task updates from the service (consolidated, debounced)
    this.taskService.taskUpdates$
      .pipe(
        takeUntil(this.destroy$),
        filter(updated => updated),
        debounceTime(500)
      )
      .subscribe(() => {
        this.updateView(false, true);
        if (this.shouldFetchTaskCounts()) {
          this.taskService.getTaskCounts();
        }
      });

    // Subscribe to search term changes with debounce
    this.searchSubject
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(300),
        distinctUntilChanged()
      )
      .subscribe(term => {
        this.searchTerm = term;
        this.currentPage = 1; // Reset to first page on search
        this.fetchTasks();
      });

    // Set up interval to update time displays
    this.timeUpdateSubscription = interval(60000) // Update every minute
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateTimeSinceRequest();
      });

    // Subscribe to task counts
    this.totalTasksRead$ = this.taskService.totalTasksRead$
      .pipe(
        takeUntil(this.destroy$),
        tap(counts => this.updateTaskCounts(counts))
      );

    // Time update subscription
    this.timeUpdateSubscription = interval(30000).pipe(
        takeUntil(this.destroy$),
        tap(() => {
          this.updateTimeSinceRequest();
          this.cdr.detectChanges();
        })
    ).subscribe();

    // Total tasks subscription
    this.totalTasks$.pipe(
        takeUntil(this.destroy$)
    ).subscribe(total => {
      this.totalPages = total;
      this.cdr.detectChanges();
    });

    // Sublet options subscription
    this.subletService.selectedOptions$.pipe(
        takeUntil(this.destroy$)
    ).subscribe(options => {
        this.handleSubletOptionsUpdate(options);
    });

    // Search subject subscription
    this.searchSubject.pipe(
        takeUntil(this.destroy$),
        debounceTime(300)
    ).subscribe(() => {
      this.filterOptions();
    });

    // Task counts subscription
    this.totalTasksRead$.pipe(
        takeUntil(this.destroy$)
    ).subscribe((counts: TaskCounts) => {
        this.updateTaskCounts(counts);
    });
  }

  private handleSubletOptionsUpdate(options: Sublet[]): void {
    this.selectedSubletOptions = options;
    if (this.isEditMode && this.updTask) {
      // Store all current values before any updates
      const currentRequest = this.updTask.request;
      const currentRequestName = this.requestName;
      const currentEmployee = this.updTask.employee;
      const currentSubletVendorAssignments = [...(this.updTask.subletVendorAssignments || [])];

      const subletIds = options.map(option => option._id);
      const selectedSublets = options.map(option => ({
        _id: option._id,
        name: option.name,
        subcategories: Array.isArray(option.subcategories) ? option.subcategories : []
      }));
      const nextAssignments = options.map(option => ({
        subletId: option._id || '',
        subletName: option.name,
        isAssigned: false,
        vendor: null
      }));

      // Keep objects on the open task. The API stores ids only.
      this.updTask.sublets = selectedSublets;
      this.updTask.subletVendorAssignments = nextAssignments;

      this.taskService.updateTask(
        <string>this.updTask?._id,
        { ...this.updTask, sublets: subletIds } as Task,
        'Sublet Update',
        'update'
      ).subscribe({
        next: (data: { task?: Task }) => {
          // Status updates return { message, task }. Assigning the wrapper
          // left sublets undefined and the detail template crashed on .length.
          const updatedTask = data?.task;
          if (updatedTask?._id) {
            this.updTask = updatedTask;
          }

          this.updTask.request = currentRequest;
          this.requestName = currentRequestName;
          this.updTask.employee = currentEmployee;
          this.updTask.sublets = selectedSublets;

          if (options.length === 0 && currentSubletVendorAssignments.length > 0) {
            this.updTask.subletVendorAssignments = currentSubletVendorAssignments;
          } else {
            this.updTask.subletVendorAssignments = nextAssignments;
          }

          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error updating task:', error);
          this.showError('Error updating sublets');
        }
      });
    }
  }

  private updateTaskCounts(counts: TaskCounts): void {
    // Use fresh API counts for increase detection (stale `this.*` was compared before fields were updated,
    // so rises like "incoming" after admin-to-admin assignment never triggered the chime).
    const nextMyTech = this.user?.roles?.includes('csr')
      ? counts.csrPending_card ?? 0
      : this.user?.roles?.includes('porter')
        ? counts.porterPending_card ?? 0
        : counts.myTechRequests_card ?? 0;
    const nextAllTech = counts.allTechRequests_card ?? counts.allTechRequests ?? 0;
    const nextIncoming = counts.incoming_card ?? 0;
    const nextOutgoing = counts.outgoing_card ?? 0;
    let incomingIncreased = false;
    let outgoingIncreased = false;

    if (this.hasEstablishedTaskCountBaseline) {
      const myTechRequestsIncreased = nextMyTech > this.prevMyTechRequestsCount;
      const allTechRequestsIncreased = nextAllTech > this.prevAllTechRequestsCount;
      incomingIncreased = nextIncoming > this.prevIncomingCardCount;
      outgoingIncreased = nextOutgoing > this.prevOutgoingCardCount;

      if (
        myTechRequestsIncreased ||
        allTechRequestsIncreased ||
        incomingIncreased ||
        outgoingIncreased
      ) {
        this.soundService.playSound('notification');
      }
    } else {
      this.hasEstablishedTaskCountBaseline = true;
    }

    // Update the counts as usual
    this.newIncomingTasksCount = counts.incoming;
    this.newOutgoingTasksCount = counts.outgoing;
    this.newTechRequestsCount = counts.myTechRequests;
    this.newTechQueueCount = counts.techQueue;
    this.allTechRequestsCount = counts.allTechRequests;
    this.allTechRequestsCount_cardCount = counts.allTechRequests_card;
    this.overDueTechRequestsCount = counts.overDueTechRequests;
    this.overDueTechRequestsByRoleCount = counts.overDueTechRequestsByRole;
    this.incoming_cardCount = counts.incoming_card;
    this.outgoing_cardCount = counts.outgoing_card;
    this.myTechRequests_cardCount = counts.myTechRequests_card;
    this.csrOpenTasksCount = counts.csrPending;
    this.csrOpenTasksCount_cardCount = counts.csrPending_card;
    this.porterPending = counts.porterPending;
    this.porterPending_cardCount = counts.porterPending_card;
    this.cdr.markForCheck();

    this.prevMyTechRequestsCount = nextMyTech;
    this.prevAllTechRequestsCount = nextAllTech;
    this.prevIncomingCardCount = nextIncoming;
    this.prevOutgoingCardCount = nextOutgoing;

    if (this.hasEstablishedTaskCountBaseline) {
      if (incomingIncreased && this.activeFilter === 'IR') {
        this.fetchTasks(true);
      }
      if (outgoingIncreased && this.activeFilter === 'MR') {
        this.fetchTasks(true);
      }
    }
  }

  ngAfterViewInit(): void {
    // Removed call to updateView() to prevent duplicate fetchTasks on load
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    // Emit value and complete the destroy subject
    this.destroy$.next();
    this.destroy$.complete();

    // Clear any timers
    if (this.tooltipTimer) {
      clearTimeout(this.tooltipTimer);
    }
    if (this.pollingTickInterval) {
      clearInterval(this.pollingTickInterval);
    }
    this.liveSync.unbindTaskRefreshSocket(this.ws, this.dashboardSocketHandlers);
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
    }
    
    // Cancel any active requests
    Object.values(this.activeRequests).forEach(subscription => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
  }

  fetchLatestTask(taskId: string) {
    this.taskService.getTask(taskId).subscribe(task => {
      this.openEditSlideOut(task);
    });
  }

 
  setActiveButton(button: HTMLButtonElement, tag: string) {
    this.lastActiveButton = button;
    if (this.activeFilter !== tag) {
      this.changeFilter(tag);
    }
    button.focus();
  }


  get isAllowedToAssign(): boolean { 
    if (this.authService.hasRoleAry(['estimator','parts coordinator'])) {
      return this.isUserAssigned;
    }
    return false;
  }

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  fetchTasks(forceRefresh = false) {
    if (
      this.user &&
      (this.user.roles.includes('am') ||
       this.user.roles.includes('vendor') ||
       this.user.roles.includes('tech'))
    ) {
      return; // Prevent API calls for AM, Vendor, and Tech roles
    }
    const cacheKey = this.activeFilter;
    const now = Date.now();
    
    // Cancel any existing request for this filter
    if (this.activeRequests[cacheKey]) {
      this.activeRequests[cacheKey].unsubscribe();
    }
    
    // Check cache
    if (!forceRefresh &&
        this.taskCache[cacheKey]?.[this.currentPage] && 
        this.lastFetchTime[cacheKey]?.[this.currentPage] && 
        (now - this.lastFetchTime[cacheKey][this.currentPage] < this.CACHE_DURATION)) {
      const tasks = this.taskCache[cacheKey][this.currentPage];
      this.processTasks(tasks);
      this.isPaginationLoading = false;
      return;
    }

    this.isLoading = false;
    this.isPaginationLoading = true;

    let taskObservable: Observable<any>;
    
    // Get the appropriate observable based on filter
    switch(this.activeFilter) {
      case 'GMC':
        taskObservable = this.taskService.getAllTechRequests(this.user._id, this.currentPage, this.limit);
        break;
      case 'CSR':
        taskObservable = this.taskService.getCSRPendingTasks(this.currentPage, this.limit);
        break;
      case 'PR':
        taskObservable = this.taskService.getPorterPendingTasks(this.currentPage, this.limit);
        break;
      case 'TR':
        taskObservable = this.taskService.getAllTechTasksByUser(this.user._id, this.currentPage, this.limit);
        break;
      case 'IR':
        taskObservable = this.taskService.getAllTasksByUser(this.user._id, this.currentPage, this.limit);
        break;
      case 'MR':
        taskObservable = this.taskService.getAllTasksCreatedByUser(this.user._id, this.currentPage, this.limit);
        break;
      default:
        taskObservable = this.taskService.getTasks(this.currentPage, this.limit);
    }

    // Add timeout to show error if request takes too long
    const timeoutObservable = timer(10000).pipe(
      tap(() => {
        this.sysNotifyService.showWarning('Request is taking longer than expected...', 3000, 'right', 'top');
      })
    );

    // Store the subscription
    this.activeRequests[cacheKey] = taskObservable.pipe(
      timeout(15000),
      takeUntil(timeoutObservable),
      takeUntil(this.destroy$), // Unsubscribe on destroy
      tap((data: any) => {
        // Update loading state immediately when data arrives
        this.isPaginationLoading = false;
        this.paginationDirection = null;

        if (!this.taskCache[cacheKey]) {
          this.taskCache[cacheKey] = {};
          this.lastFetchTime[cacheKey] = {};
        }
        
        // Apply request type filter if selected
        let filteredTasks = data.tasks;
        if (this.selectedRequestType) {
          filteredTasks = data.tasks.filter((task: any) => task.request._id === this.selectedRequestType);
        }
        
        this.taskCache[cacheKey][this.currentPage] = filteredTasks;
        this.lastFetchTime[cacheKey][this.currentPage] = now;
        
        this.processTasks(filteredTasks);
      }),
      catchError(error => {
        console.error('Error fetching tasks:', error);
        // Reset loading state immediately on error
        this.isPaginationLoading = false;
        this.paginationDirection = null;
        
        if (error instanceof TimeoutError) {
          this.sysNotifyService.showError('Request timed out. Please try again.', 3000, 'right', 'top');
        } else {
          this.sysNotifyService.showError('Error fetching tasks', 3000, 'right', 'top');
        }
        return of([]);
      }),
      finalize(() => {
        // Cleanup only - loading states already handled
        delete this.activeRequests[cacheKey];
      })
    ).subscribe();
  }

  updateView(resetPage: boolean = false, forceRefresh = false) {
    if (resetPage) {
      this.currentPage = 1;
    }
    this.fetchTasks(forceRefresh);
  }

  

  filterTasksByUser(): void {
    this.currentPage = 1; // Reset to first page when switching tabs
    this.fetchTasks(); // Use the unified fetchTasks method instead
  }

  filterTasksCreatedByUser(): void {
    this.isLoading = true; 
    this.taskService.getAllTasksCreatedByUser(this.user._id, this.currentPage, this.limit);
  }

  filterTasksByShop(): void {
    this.taskService.getTasks(this.currentPage, this.limit);
  }

  filterAllTechTasksByUser(): void {
    this.isLoading = true;
    if (this.user && this.user._id) {
      this.taskService.getAllTechTasksByUser(this.user._id, this.currentPage, this.limit);
    }
  }

  filterTaskByTechTaskRoleAndUser() {
    if (this.user?.roles.includes('gm')) {
      // For GM role - get all tech requests
      this.isLoading = true;
      this.taskService.getAllTechRequests(this.user._id, this.currentPage, this.limit);
    } else {
      // Existing logic for regular users
      this.isLoading = true;
      this.taskService.getAllTechTasksByUser(this.user._id, this.currentPage, this.limit);
    }
  }

  updateTimeSinceRequest() {
    this.tasks$.subscribe(tasks => {
      tasks.forEach((task) => {
        task.timeSinceRequest = this.calculateTimeSinceRequest(task.createdAt);
        task.flag = this.calculateFlag(task.createdAt, task.taskCompleted);
      });
      this.cdr.detectChanges(); // Trigger change detection
    });
  }

  acknowledgeCompletion(task: Task, event: any) {
    event.preventDefault();
    event.stopPropagation();
    this.showCompleteTaskModal = true;
    this.cdr.detectChanges();    
  }

  closeCompleteTaskModal() {
    this.showCompleteTaskModal = false;
    this.cdr.detectChanges();
  }

  completeTask(task: Task) {
    this.showCompleteTaskModal = false;
    const action = 'complete';
    task.taskCompleted = true;
    task.estimatorTimeStamp = moment().toDate(); // Set the timestamp to the current date and time as a Date object
    task.timeToComplete = this.calculateTimeToComplete(task.createdAt, task.estimatorTimeStamp);
    task.flag = this.calculateFlag(task.createdAt, task.taskCompleted); // Recalculate the flag
    this.taskService.updateTask(<string>task._id, task, '', action).subscribe((updatedTask) => {
      this.toggleSlideOut();
      this.updateTaskInCache(updatedTask);
      this.invalidateCache(this.activeFilter);
      this.cdr.markForCheck();
    });
  }

  calculateTimeToComplete(start: Date | undefined, end: Date): string {
    const duration = moment.duration(moment(end).diff(moment(start)));
    const hours = Math.floor(duration.asHours()).toString().padStart(2, '0');
    const minutes = duration.minutes().toString().padStart(2, '0');
    const seconds = duration.seconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }

  calculateTimeSinceRequest(start: Date | undefined): string { 
    const now = moment();
    const duration = moment.duration(now.diff(moment(start)));
    const hours = Math.floor(duration.asHours()).toString().padStart(2, '0');
    const minutes = duration.minutes().toString().padStart(2, '0');
    const seconds = duration.seconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }

  calculateFlag(createdAt: Date | undefined, taskCompleted: boolean): string {
    if (taskCompleted) {
      return 'green';
    }
    const now = moment();
    const durationInMinutes = moment.duration(now.diff(moment(createdAt))).asMinutes();
    if (durationInMinutes > 30) {
      return 'red';
    } else if (durationInMinutes > 15) {
      return 'yellow';
    } else {
      return 'white';
    }
  }

  formatDate(date: Date | undefined) {
    return moment(date).format('MM/DD/YY, h:mm A');
  }

  createTask(task: Task) {
    if (!task.taskCompleted && !task.estimatorTimeStamp) {
      task.estimatorTimeStamp = new Date();
    }
    this.taskService.createTask(task).subscribe(() => {
      this.fetchTasks();
    });
  }

  updateTask(task: Task) {
    if (!task.taskCompleted && !task.estimatorTimeStamp) {
      task.estimatorTimeStamp = new Date();
    }
    this.taskService.updateTask(<string>task._id, task, 'update').subscribe((updatedTask) => {
      this.updateTaskInCache(updatedTask);
      this.cdr.markForCheck();
    });
  }

  assignTaskToUser(task: Task, action: boolean, event?: Event) { 
    if (event) {
      event.preventDefault();
    }
    let msg: string = action ? `Are you sure you want to assign this request to you ?` : `Are you sure you want to unassign this request to you ?`
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '370px',
      data: {
        message: msg,
        btn: action ? `Assign Request` : `Unassign Request`,
        task: task // Passing the task object if needed for further actions
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        task.isRead = action ? false : true;
        this.taskService.assignTaskToUser(task, action).subscribe((res) => {
          // Removed this.activeFilter = 'TR'; to preserve current filter
          this.fetchTasks();
          this.snackBar.open('Request assigned successfully', 'Close', {
            duration: 3000, // Duration in milliseconds
            verticalPosition: 'top', // Position on the screen
            horizontalPosition: 'right',
            panelClass: ['custom-snackbar-container']
          });
        });
      } else {
        this.cdr.markForCheck();
      }
    });

  }
  
  nextPage(scrollToTop = false) {
    if (this.currentPage < this.totalPages && !this.isPaginationLoading) {
      this.isPaginationLoading = true;
      this.paginationDirection = 'forward';
      this.currentPage++;
      this.fetchTasks();
      if (scrollToTop) {
        this.scrollTaskListIntoView();
      }
    }
  }

  previousPage(scrollToTop = false) {
    if (this.currentPage > 1 && !this.isPaginationLoading) {
      this.isPaginationLoading = true;
      this.paginationDirection = 'backward';
      this.currentPage--;
      this.fetchTasks();
      if (scrollToTop) {
        this.scrollTaskListIntoView();
      }
    }
  }

  private scrollTaskListIntoView() {
    requestAnimationFrame(() => {
      document.getElementById('task-list-top')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    });
  }

  
  navigateToAddTask() {
    this.router.navigate(['/tasks/new']);
  }

  loadRequests() {
    const prevRequest = this.task.request;
    const prevEmployee = this.task.employee;
    const prevSublets = Array.isArray(this.task.sublets) ? [...this.task.sublets] : [];
    this.requestService.getRequests('admin').subscribe((data: any) => {
      this.requests = data.sort((a: any, b: any) => a.name.localeCompare(b.name));
      // Always restore as string _id
      let prevRequestId: string = '';
      if (isRequestWithId(prevRequest)) {
        prevRequestId = prevRequest._id;
      } else {
        prevRequestId = prevRequest;
      }
      const found = this.requests.find((req: any) => req._id === prevRequestId);
      if (found) {
        this.task.request = found._id;
      } else {
        this.task.request = '';
      }
      // Restore employee if still exists in filteredUsers/globalGroups/privateGroups
      if (prevEmployee) {
        const allEmployees = [
          ...this.filteredUsers,
          ...this.filteredGlobalGroups,
          ...this.filteredPrivateGroups
        ];
        if (allEmployees.some((emp: any) => emp._id === prevEmployee)) {
          this.task.employee = prevEmployee;
        } else {
          this.task.employee = '';
        }
      } else {
        this.task.employee = '';
      }
      // Restore sublets if still exist in selectedSubletOptions
      if (prevSublets.length > 0 && Array.isArray(this.selectedSubletOptions)) {
        const validSublets = prevSublets.filter(subletId =>
          this.selectedSubletOptions.some(option => option._id === subletId)
        );
        if (validSublets.length > 0) {
          this.task.sublets = validSublets;
        } else {
          this.task.sublets = [];
        }
      } else {
        this.task.sublets = [];
      }
    });
  }

  loadEmployees() {
    this.userService.getUsersByTenant(this.getTenantContext()).subscribe((users: any[]) => {
      this.tenantUsers = users
      .filter((user: { roles: any[]; }) => !user.roles.some(role => role.name === 'developer'))
      .sort((a: any, b: any) => a.name.localeCompare(b.name));
      this.filterOptions(); // Update the options list after sorting
    });
  }

  toggleSlideOut() {
    this.slideOutVisible = false;
    this.slideOutVisible2 = false;
    //this.slideOutVisible3 = false;
    this.isEditMode = false;
    this.resetTask();
    if (this.lastActiveButton) {
      this.lastActiveButton.focus();
    }
    this.loadRequests();
  }

  toggleVendorSlideOut() { 
   
    this.slideOutVisible4 = !this.slideOutVisible4;
    
    // When closing the vendor slideout, also close other slideouts and reset states
    if (!this.slideOutVisible4) {
      this.slideOutVisible = false;
      this.slideOutVisible2 = true;
      this.slideOutVisible3 = false;
      this.updateView();
    }
    
  }

  subletSelect(sublet: SubletVendorAssignment) { 
    this.subletSelected = sublet;
    this.slideOutVisible4 = true;
    this.slideOutVisible2 = true;
    this.slideOutVisible3 = false;
  }

  openAddRequestSlideOut() {
    this.isEditMode = false;
    this.slideOutVisible = true;
    this.loadRequests(); 
  }

 
  
  openEditSlideOut(task: Task, event?: Event) { 
    // Mark as read in cache and UI
    if (task._id) {
      this.updateTaskReadStatusInCache(task._id);
    }
    if(event) {
      event.stopPropagation();
      event.preventDefault();
    }
    this.updTask = { ...task };

    // Track the previous assignee as a string id
    if (task.employee && typeof task.employee === 'object' && task.employee._id) {
      this.previousAssignee = task.employee._id;
    } else if (typeof task.employee === 'string') {
      this.previousAssignee = task.employee;
    } else {
      this.previousAssignee = null;
    }

    // Ensure updTask.sublets is initialized as an array
    if (!Array.isArray(this.updTask.sublets)) {
      this.updTask.sublets = [];
    }

    // Repopulate sublets with necessary data
    this.updTask.sublets = this.updTask.sublets.map(sublet => ({
      ...sublet,
      subcategories: Array.isArray(sublet.subcategories) ? sublet.subcategories : []
    }));
    
    this.tooltipVisible = false;
    this.isEditMode = true;
    this.slideOutVisible2 = true;
    this.loadingComments = true;
  
    // Mark notification as read
    this.notificationService.markAsReadByTaskId(<string>task._id);
  
    // Ensure that requests are loaded before setting the dropdown value
    if (!this.requests || this.requests.length === 0) {
      this.requestService.getRequests('admin').subscribe((data: any) => {
        this.requests = data.sort((a: any, b: any) => a.name.localeCompare(b.name));
        this.setRequestDropdownValue(task);
      });
    } else {
      this.setRequestDropdownValue(task);
    }
  
    // If task has a distribution group, assign it, otherwise assign the employee
    if (task.distro !== null) {
      this.updTask.employee = task.distro;
    } else if (task.employee) {
      const matchedUser = this.tenantUsers.find(user => task.employee && user._id === task.employee._id);
      if (matchedUser) {
        this.updTask.employee = matchedUser._id;
      }
    }
  
    // Fetch comments from cache or backend
    this.commentService.getComments(<string>task._id).subscribe({
      next: (data: any) => {
        this.updTask.commentMsg = data;
        this.loadingComments = false;
      },
      error: (err: any) => {
        console.error('Error loading comments', err);
        this.showError(`Error loading comments: ${err.message}`);
        this.loadingComments = false;
      }
    });

    // Refresh the list but preserve current filter and page
    this.fetchTasks();

    this.cachedMedia = [...(task.media || [])];

    if (!this.updTask.media || this.updTask.media.length === 0) {
      this.updTask.media = [...this.cachedMedia];
    }
    this.refreshTaskLevelMediaStrip();

    if (task._id) {
      this.taskService.getTask(task._id).subscribe({
        next: (fullTask) => {
          if (fullTask?.media?.length) {
            this.updTask.media = fullTask.media;
            this.cachedMedia = [...fullTask.media];
            this.refreshTaskLevelMediaStrip();
            this.cdr.markForCheck();
          }
        },
        error: () => {
          this.refreshTaskLevelMediaStrip();
          this.cdr.markForCheck();
        },
      });
    }
  }

  private setRequestDropdownValue(task: Task) {
    let matchedRequest;
    if (isRequestWithId(task.request)) {
      matchedRequest = this.requests.find(req => req._id === task.request._id);
    } else {
      matchedRequest = this.requests.find(req => req._id === task.request);
    }
    if (matchedRequest) {
      this.updTask.request = matchedRequest._id;
      this.requestName = matchedRequest.name;
    } else if (isRequestWithId(task.request)) {
      const cachedTechRequest = this.techRequestCache[task.request._id];
      if (cachedTechRequest) {
        this.requestName = cachedTechRequest.name;
        // Add to requests if not already present
        if (!this.requests.some(req => req._id === cachedTechRequest._id)) {
          this.requests = [...this.requests, cachedTechRequest];
        }
        this.updTask.request = cachedTechRequest._id;
      } else {
        this.requestService.getRequestById(task.request._id).subscribe(req => {
          if (req && req._id) {
            this.techRequestCache[req._id] = req;
            this.requestName = req.name;
            // Add to requests if not already present
            if (!this.requests.some(r => r._id === req._id)) {
              this.requests = [...this.requests, req];
            }
            this.updTask.request = req._id;
            // Fallback: force change detection after a short delay
            setTimeout(() => {
              this.cdr.detectChanges();
            }, 100);
          }
        });
      }
    }
    // Fallback: force change detection after a short delay
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 100);
  }

  openEditCommentView(updTask: Task, mode: any) { 
    this.slideOutVisible3 = true;
    this.isEditMode = mode;
    this.loadingComments = false;
   // timer(500).subscribe(()=> this.loadingComments = false); // Show loader for comments
    // Ensure that `updTask.request` is set to the corresponding request object
    const matchedRequest = this.requests.find(req => req._id === updTask.request);
    if (matchedRequest) {
        updTask.request = matchedRequest._id;
      this.requestName = matchedRequest.name;
    }
    this.cdr.detectChanges();
  }

  closeEditCommentView(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    this.slideOutVisible3 = false;
    // Only show detail view if it wasn't a long press
    this.slideOutVisible2 = !this.isLongPress;
    this.isEditMode = !this.isLongPress;
    // Reset long press state
    this.isLongPress = false;
    // Clear any comment-related temporary states if needed
    this.loadingComments = false;
  }

  onSubmitComment(task: Task, state?: boolean, event?: Event) {
    this.isEditMode = true;
    this.onSubmit(state);
    this.toggleSlideOut3(event!);
  }
  
  
  resetTask() {
    this.task = {
      employee: '',
      request: '',
      comments: '',
      isUrgent: false,
      tenant: this.getTenantContext(),
      sublets: []
    };
    this.subletService.resetOptions(); // Reset selected sublets
  }

  async onSubmit(isUrgent: boolean = false) {
    if (this.isEditMode) {
      if (this.updTask.comments !== '' && isUrgent === false) {
        const newComment = {
          user: this.usr,
          content: <string>this.updTask.comments,
          timestamp: new Date()
        };
        this.updTask.commentMsg.unshift(newComment);
        this.updTask.comments = '';
        this.isFocused = false;
        this.cdr.markForCheck();
      
        this.taskService.addTaskComment(<string>this.updTask?._id, {
          comment: newComment.content,
          timestamp: newComment.timestamp
        }).subscribe((updatedTask) => {
          this.updateTaskInCache(updatedTask.task);
          this.cdr.markForCheck();
        });
      } else {
        if (this.updTask.comments !== '' && isUrgent === true) {
          this.updTask.commentMsg.unshift({
            user: this.usr,
            content: <string>this.updTask.comments,
            timestamp: new Date()
          });
        }
        // Set isRead to false if task is being assigned to someone
        if (this.updTask.employee) {
          this.updTask.isRead = false;
        }
        // Check if the new assignee is a distro group
        // const isDistroGroup = [
        //   ...this.filteredGlobalGroups,
        //   ...this.filteredPrivateGroups
        // ].some(group => group._id === this.updTask.employee);
        // Compare as string ids only
        // let currentAssigneeId: string | null = null;
        // if (this.updTask.employee && typeof this.updTask.employee === 'object' && this.updTask.employee._id) {
        //   currentAssigneeId = this.updTask.employee._id;
        // } else if (typeof this.updTask.employee === 'string') {
        //   currentAssigneeId = this.updTask.employee;
        // }
        // const isReassigning = this.previousAssignee !== currentAssigneeId;
        // if (isReassigning && isDistroGroup) {
        //   this.pendingDistroTaskData = { ...this.updTask };
        //   this.showDistroGroupTaskModal = true;
        //   return;
        // }
        this.updTask.comments = '';
        this.taskService.updateTask(<string>this.updTask?._id, this.updTask, "Updating Task", "update").subscribe((updatedTask) => {
          this.toggleSlideOut();
          this.updateTaskInCache(updatedTask.task);

          this.updTask.comments = '';
          this.cdr.markForCheck();
          // Reset previousAssignee after update
          this.previousAssignee = null;
        });
      }
    } else {
      const isDistroGroup = [
        ...this.filteredGlobalGroups,
        ...this.filteredPrivateGroups
      ].some(group => group._id === this.task.employee);

      const taskData: any = {
        employee: this.task.employee,
        request: this.task.request,
        comments: this.task.comments,
        tenant: this.getTenantContext(),
        isUrgent: this.task.isUrgent,
        isRead: this.task.employee ? false : true,
        sublets: this.subletService.selectedOptionsSubject.value.map(option => option.name)
      };

      if (isDistroGroup) {
        this.openDistroGroupTaskModal(taskData);
        return;
      }

      this.taskService.createRequest(taskData).subscribe({
        next: () => {
          this.applyAfterLocalCreate();
          this.toggleSlideOut();
          this.sysNotifyService.showSuccess('Request created successfully', 3000, 'right', 'top');
        },
        error: (error) => {
          console.error('Error creating request:', error);
          this.sysNotifyService.showSuccess('Error creating request', 3000, 'right', 'top');
        }
      });
    }
  }

  acknowledgeDeleteTask(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.showDeleteTaskModal = true;
    this.cdr.detectChanges();
  }

  closeDeleteTaskModal() {
    this.showDeleteTaskModal = false;
    this.cdr.detectChanges();
  }

  deleteTask(task: Task) {
    this.showDeleteTaskModal = false;
    this.taskService.deleteTask(task._id).subscribe(() => {
      this.toggleSlideOut();
      this.slideOutVisible2 = false;
      this.runLightRefresh(true);
      this.cdr.markForCheck();
    });
  }

  /** After local create: HTTP path updates this client; peers are notified by server taskUpdate. */
  private applyAfterLocalCreate(): void {
    this.invalidateCache(this.activeFilter);
    this.updateView(false, true);
    if (this.shouldFetchTaskCounts()) {
      this.taskService.getTaskCounts();
    }
    this.loadRequests();
    this.lastUpdateTime = new Date();
    this.cdr.markForCheck();
  }

  trackByFn(index: number, item: any) {
    return item._id; // or item.id, depending on your schema
  }

  addBtnName(): string {
    return !this.authService.hasRole('tech') ? 'Create Request' : 'Create Request';
  }

  toggleUrgency(): void {
    this.task.isUrgent = !this.task.isUrgent;
  }

  
  toggleSubletComponent() {
    this.subletService.toggleSublet();
    this.syncSelectedSublets();
  }

  syncSelectedSublets() {
    const sublets = Array.isArray(this.updTask?.sublets) ? this.updTask.sublets : [];
    const selectedSublets = sublets
      .map(sublet => ({
      _id: sublet._id,
      name: sublet.name,
      selected: true,
      subcategories: Array.isArray(sublet.subcategories) ? sublet.subcategories
      .filter((subcategory: { isCategory: boolean }) => !subcategory.isCategory) // Filter out subcategories that are categories
      .map((subcategory: { name: any; selected: any; }) => ({
        name: subcategory.name,
        selected: subcategory.selected,
        isSubcategory: true
      })) : [], // Ensure subcategories is an array
      source: 'dashboard'
    }));
    this.subletService.selectedOptionsSubject.next(selectedSublets);
  }


  addSubletOption(option: any) {
    this.selectedSubletOptions.push(option);
  }

  // Remove sublet options from the array
  removeSubletOption(option: any) {
    this.subletService.removeOption(option);
  }

  showAddSublet(requestId: any): boolean {
    if (!requestId || requestId === '') {
      return false;
    }
    const matchedRequest = this.requests.find(req => req._id === requestId);
    if (!matchedRequest) {
    return false;
    }
    const requestName = matchedRequest.name;
    return requestName === "Vehicle Complete" || requestName === "Supplement" || requestName === "Phase Change";
  }


  getFlagIcon(flag: string): string {
    switch (flag) {
      case 'red':
        return 'alert-circle-outline'; // or 'exclamation-circle' for FontAwesome
      case 'yellow':
        return 'warning-outline'; // or 'exclamation-triangle'
      case 'green':
        return 'checkmark-circle-outline'; // or 'check-circle'
      default:
        return '';
    }
  }

  getFlagText(flag: string): string {
    return this.flagTextMap[flag];
  }

  getFlagTooltip(flag: string): string {
    switch (flag) {
      case 'red':
        return 'This task is overdue and requires immediate attention.';
      case 'yellow':
        return 'This task is approaching its deadline.';
      case 'green':
        return 'This task is on track.';
      default:
        return '';
    }
  }

  // Filter options based on search term
  filterOptions() {
    const searchLower = this.searchTerm.toLowerCase();

    // Filter Global Groups
    this.filteredGlobalGroups = this.globalGroups.filter(group => 
      group.name.toLowerCase().includes(searchLower)
    );

    // Filter Private Groups
    this.filteredPrivateGroups = this.privateGroups.filter(group => 
      group.name.toLowerCase().includes(searchLower)
    );

    // Filter Users
    
    this.filteredUsers = this.tenantUsers.filter(user => 
      user.name.toLowerCase().includes(searchLower) && 
      !this.excludedNames.includes(user.name) && // Exclude specific names
      !user.roles.some((role: { name: string }) => 
        role.name === 'developer' || role.name === 'tech'
    ) && 
      user.isActive && 
      user._id !== this.user._id
      );
  }

  // Trigger search on input change
  onSearchInput(event: any) {
    this.searchTerm = event.target.value;
    this.searchSubject.next(this.searchTerm);
  }

  // Update selected option text when user selects an option
  selectOption(option: string) {
    this.selectedOption = option;
    this.dropdownAssignedToOpen = false; // Close dropdown after selection
  }

  clearPlaceholder() {
    this.isFocused = true;
  }

  restorePlaceholder() {
    // Check if there's any text entered, if not, restore the placeholder
    if (!this.updTask.comments || this.updTask.comments.trim() === '') {
      this.isFocused = false;
    }
  }

  toggleCommentSection() {
    this.commentSectionVisible = !this.commentSectionVisible;
  }

  toggleSlideOut3(event?: Event) {
   if(event) {
    event.stopPropagation();
    event.preventDefault();
   }
    this.slideOutVisible3 = !this.slideOutVisible3;
  }

  showSuccess(msg: string) {
    this.sysNotifyService.showSuccess(msg, 3000, 'right', 'top');
  }

  showError(msg: string) {
    this.sysNotifyService.showError(msg, 3000, 'right', 'top');
  }

  showWarning(msg: string) {
    this.sysNotifyService.showWarning(msg, 3000, 'right', 'top');
  }


  // Handle image uploads
  onImageUpload(event: Event, index: number) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      this.uploadedImages[index] = file;
      this.imagePreviews[index] = URL.createObjectURL(file);
    }
  }

  openInviteVendor() {
    this.isInviteVendorModalVisible = true;
    this.vendorsService.openInviteVendorPanel();
  }

  // Close Modal
  closeInviteVendorModal() {
    this.isInviteVendorModalVisible = false;
    this.inviteVendorForm.reset(); // Reset the form when closing
    this.vendorsService.closeInviteVendorPanel();
  }

   // Submit Invite Vendor Form
   submitInviteVendor() {
    if (this.inviteVendorForm.valid ) {
      this.vendorsService.createTempVendor(this.inviteVendorForm.value)
        .subscribe({
        next: (data) => {
          this.showSuccess('Vendor added successfully');
          this.closeInviteVendorModal();
          },
          error: (err) => {
            this.showError('Error adding vendor');
          }
      })
      
    }
   }
  
   // Method to handle vendor selection
   selectVendor(vendor: any) {
    if (!this.selectedVendors.includes(vendor)) {
      this.selectedVendors.push(vendor);
    }
  }

  // Method to remove a vendor from the selected list
  removeVendor(vendor: any) {
    this.selectedVendors = this.selectedVendors.filter(v => v !== vendor);
  }

  // Method to reset the vendor association view
  resetVendorAssociationView() {
    this.selectedVendors = [];
    this.vendorRequestDetails = '';
    this.vendorRequestRONumber = '';
    this.uploadedImages = [];
    this.imagePreviews = [];
    this.searchQuery = '';
  }

  updateFormValues() {
    this.vendorWorkForm.patchValue({
      requestDetails: this.vendorRequestDetails,
      roNumber: this.vendorRequestRONumber,
      selectedVendors: this.selectedVendors
    });
  }
  
  sendToVendors() {
    const taskId = this.updTask._id!.toString();
    const formData = new FormData();
    this.updateFormValues();
    
    if (this.vendorWorkForm.valid) {
      Object.keys(this.vendorWorkForm.value).forEach(key => {
        if (key === 'selectedVendors') {
          formData.append(key, JSON.stringify(this.vendorWorkForm.value[key]));
        } else {
          formData.append(key, this.vendorWorkForm.value[key]);
        }
      });

      this.uploadedImages.forEach((file: File, index: number) => {
        formData.append(`file${index}`, file);
      });
      // Send to the backend
      this.vendorsService.imageUpload(formData).subscribe({
        next: () => {
          this.showSuccess('Files uploaded successfully');
        },
        error: () => {
         this.showError('Error uploading files');
        }
      });
    }
  }

  removeImage(index: number) {
    this.imagePreviews[index] = '';
  }

  toggleGMView() {
    this.isGMView = !this.isGMView;
  }

  // getCommentUserName(comment: any): string {
  //   if (comment.user?.name) {
  //     return comment.user.name;
  //   } else if (comment.user?.firstName && comment.user?.lastName) {
  //     return `${comment.user.firstName} ${comment.user.lastName}`;
  //   } else {
  //     return 'Unknown User';
  //   }
  // }
  
  // getCommentUserInitial(comment: any): string {
  //   if (comment.user?.name) {
  //     return comment.user.name.charAt(0);
  //   } else if (comment.user?.firstName) {
  //     return comment.user.firstName.charAt(0);
  //   } else {
  //     return '?';
  //   }
  // }

  // Modify the getCommentUserName method
getCommentUserName(comment: any): string {
  // If comment.user is a string (ID), try to get from cache
  if (typeof comment.user === 'string') {
    const cachedUser = this.userCache[comment.user];
    if (cachedUser) {
      return cachedUser.name || 'Unknown User';
    }
    // Fetch user data if not in cache
    this.userService.getUserById(comment.user).subscribe(userData => {
      this.userCache[comment.user] = userData;
      this.cdr.detectChanges();
    });
    return 'Loading...';
  }
  
  // Fallback for existing logic
  if (comment.user?.name) {
    return comment.user.name;
  }
  return 'Unknown User';
}

  // Modify the getCommentUserInitial method
  getCommentUserInitial(comment: any): string {
    if (typeof comment.user === 'string') {
      const cachedUser = this.userCache[comment.user];
      if (cachedUser) {
        return cachedUser.name?.charAt(0) || '?';
      }
      return '?';
    }
    
    if (comment.user?.name) {
      return comment.user.name.charAt(0);
    }
    return '?';
  }

  isLastRow(task: Task): Observable<boolean> {
    return this.tasks$.pipe(
      map((tasks: Task[]) => tasks.indexOf(task) === 9)
    );
  }

  getCommentPreview(task: Task): string {
    if (!task.commentMsg || (task.commentMsg as Array<any>).length === 0) {
      return 'No comments yet';
    }

    // Get last 2 comments
    const recentComments = task.commentMsg.slice(0, 2);
    
    return recentComments.map(comment => {
      const userName = this.getCommentUserName(comment);
      return `${userName}: ${comment.content.substring(0, 50)}${comment.content.length > 50 ? '...' : ''}`;
    }).join('\n');
  }

  showTooltip(event: MouseEvent, task: Task) {
    event.stopPropagation();
    
    // Clear any existing timer
    if (this.tooltipTimer) {
      clearTimeout(this.tooltipTimer);
    }
    
    // Get the icon element's position
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    
    // Calculate position to show tooltip to the left of the icon
    // Reduce spacing between the icon and tooltip from 20px to 8px
    const tooltipWidth = 384; // Width of the tooltip (w-96 = 24rem = 384px)
    const spacing = 8;
    
    this.tooltipPosition = {
      x: Math.max(spacing, rect.left - tooltipWidth - spacing), // Ensure tooltip doesn't go off screen left
      y: Math.max(spacing, rect.top - 150) // Position tooltip higher up
    };
    
    this.activeTooltipTask = task;
    this.tooltipVisible = true;
  }

  onTooltipMouseEnter() {
    this.isTooltipHovered = true;
    if (this.tooltipTimer) {
      clearTimeout(this.tooltipTimer);
    }
  }

  onTooltipMouseLeave(event: MouseEvent) {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    
    // If mouse moves to the right of the icon, close immediately
    if (event.clientX > rect.right) {
        this.tooltipVisible = false;
        this.activeTooltipTask = null;
      this.isTooltipHovered = false;
      if (this.tooltipTimer) {
        clearTimeout(this.tooltipTimer);
      }
        this.cdr.detectChanges();
      }
  }

  startTooltipTimer() {
    if (this.tooltipTimer) {
      clearTimeout(this.tooltipTimer);
    }
    this.tooltipTimer = setTimeout(() => {
      if (!this.isTooltipHovered) {
        this.tooltipVisible = false;
        this.activeTooltipTask = null;
        this.cdr.detectChanges();
      }
    }, this.tooltipCloseDelay);
  }

  calculateTaskMetrics(tasks: Task[]) {
    this.taskMetrics = {
      active: tasks.filter(task => !task.taskCompleted).length,
      overdue: tasks.filter(task => 
        !task.taskCompleted && 
        moment.duration(moment().diff(moment(task.createdAt))).asMinutes() > 30
      ).length
    };
    this.cdr.detectChanges();
  }

  refreshDashboard() {
    if (!this.ws.isConnected()) {
      this.ws.connect();
      this.setupDashboardSocketRefresh();
    }
    this.runFullRefresh(true);
  }

  /** Task list + metric cards; used for socket, visibility, and WS-down polling. */
  private runLightRefresh(force: boolean): void {
    this.lastUpdateTime = new Date();
    this.invalidateCache(this.activeFilter);
    this.fetchTasks(force);
    if (this.shouldFetchTaskCounts()) {
      this.taskService.getTaskCounts();
    }
    this.cdr.detectChanges();
  }

  /** Cheap stale check while WebSocket is up (no full task fetch). */
  private runCountsRefresh(): void {
    this.lastUpdateTime = new Date();
    if (this.shouldFetchTaskCounts()) {
      this.taskService.getTaskCounts();
    }
    this.cdr.detectChanges();
  }

  /** Manual refresh / keyboard shortcut: includes GM monthly report. */
  private runFullRefresh(force: boolean): void {
    this.runLightRefresh(force);
    if (this.user?.roles.includes('gm')) {
      this.loadMonthlyReport();
    }
  }

  private refreshFromSocket(payload?: TaskUpdatePayload): void {
    this.invalidateCache();
    this.taskService.triggerTaskUpdate();
    if (this.shouldFetchTaskCounts()) {
      this.taskService.getTaskCounts();
    }
    if (payload?.action === 'create') {
      this.loadRequests();
    }

    this.updateView(false, true);
    this.lastUpdateTime = new Date();
    this.cdr.detectChanges();
  }

  // Add keyboard shortcut handler
  @HostListener('window:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent) {
    // Only handle if not in input/textarea
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
      return;
    }

    if (event.ctrlKey || event.metaKey) {
      switch(event.key) {
        case 'n':
          event.preventDefault();
          this.openAddRequestSlideOut();
          break;
        case 'f':
          event.preventDefault();
          document.querySelector<HTMLInputElement>('input[type="text"]')?.focus();
          break;
        case 'r':
          event.preventDefault();
          this.refreshDashboard();
          break;
      }
    }
  }

  // Add filter methods
  filterByUrgent() {
    this.urgentFilter = !this.urgentFilter;
    this.filterChangeSubject.next();
  }

  filterByUnread() {
    this.unreadFilter = !this.unreadFilter;
    this.filterChangeSubject.next();
  }

  toggleViewMode(mode: 'list' | 'grid') {
    this.viewMode = mode;
  }

  onSearch() {
    // Implement search logic
    this.updateFilters();
  }

  private updateFilters() {
    // Implement filter logic combining search, urgent, and unread filters
  }

  showKeyboardShortcuts() {
    this.sysNotifyService.showSuccess(`
      Keyboard Shortcuts:
      Ctrl/⌘ + N: New Request
      Ctrl/⌘ + F: Focus Search
      Ctrl/⌘ + R: Refresh Dashboard
    `, 5000, 'right', 'top');
  }

  checkIfNewUser() {
    const lastLoginDate = localStorage.getItem('lastLoginDate');
    const currentDate = new Date().toISOString();
    
    if (!lastLoginDate) {
      this.isNewUser = true;
      localStorage.setItem('lastLoginDate', currentDate);
    }
  }

  // Add method to invalidate cache
  invalidateCache(filter?: string) {
    if (filter) {
      delete this.taskCache[filter];
      delete this.lastFetchTime[filter];
    } else {
      this.taskCache = {};
      this.lastFetchTime = {};
    }
  }

  private processTasks(tasks: Task[]) {
    if (!tasks) return;

    // Set loading state immediately
    this.isPaginationLoading = true;
    
    // Create a small delay to ensure loading state is visible
    setTimeout(() => {
      // Store the new tasks temporarily
      const processedTasks = tasks.map(task => ({
        ...task,
        timeSinceRequest: this.calculateTimeSinceRequest(task.createdAt),
        flag: this.calculateFlag(task.createdAt, task.taskCompleted)
      }));

      // Update available request types
      this.updateAvailableRequestTypes(processedTasks);

      if (['CTR', 'TR', 'GMC'].includes(this.activeFilter)) {
        this.calculateTaskMetrics(processedTasks);
      }

      this.isLoading = false;
      this.noData = processedTasks.length === 0;
      
      // Update tasks in a single operation
      this.taskService.updateTasks(processedTasks);
      this.isPaginationLoading = false;
      this.cdr.markForCheck();
    }, 300); // 300ms delay for smooth transition
  }

  private loadMonthlyReport() {
    const today = new Date();
    const year = today.getFullYear();
    const month = today.getMonth() + 1; // JavaScript months are 0-based

    this.dailyReportService.getMonthlyReport(this.getTenantContext(), year, month).subscribe({
      next: (response) => {
        if (response && response.monthTotals) {
          const monthTotals = response.monthTotals;
          this.monthlyBudget = monthTotals.monthlyBudget || 0;
          this.actualClosed = (monthTotals.monthlyBudget - monthTotals.actualTotal) || 0; //monthTotals.monthlyBudget - monthTotals.actualTotalClosed || 0;
          // Calculate remaining working days
          this.remainingWorkDays = (monthTotals.totalWorkingDays || 0) - (monthTotals.remainingWorkingDaysOfMonth || 0);
        } else {
          this.monthlyBudget = 0;
          this.actualClosed = 0;
          this.remainingWorkDays = 0;
        }
        this.cdr.markForCheck();
      },
      error: (error) => {
        console.error('Error loading monthly report:', error);
        this.monthlyBudget = 0;
        this.actualClosed = 0;
        this.remainingWorkDays = 0;
        this.cdr.markForCheck();
      }
    });
  }

  startLongPress(event: MouseEvent | TouchEvent, task: Task) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    // Clear any existing timeout
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
    }
    
    this.isLongPress = false;
    this.updTask = { ...task };
    
    // Set up the long press timeout
    this.longPressTimeout = setTimeout(() => {
      this.isLongPress = true;
      
      // Close other drawers first
      this.slideOutVisible = false;
      this.slideOutVisible2 = false;
      this.slideOutVisible4 = false;
      
      // Only open the comment drawer
      this.slideOutVisible3 = true;
      this.loadingComments = true;
      
      // Set the request name
      const matchedRequest = this.requests.find(req => req._id === task.request._id);
      if (matchedRequest) {
        this.requestName = matchedRequest.name;
      }
      
      // Load comments
      this.commentService.getComments(<string>task._id).subscribe({
        next: (data: any) => {
          this.updTask.commentMsg = data;
          this.loadingComments = false;
        },
        error: (err: any) => {
          console.error('Error loading comments', err);
          this.showError(`Error loading comments: ${err.message}`);
          this.loadingComments = false;
        }
      });
    }, this.LONG_PRESS_DURATION);
  }

  endLongPress(event: MouseEvent | TouchEvent) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
    }
    
    // If it wasn't a long press, handle as a normal click
    if (!this.isLongPress) {
      this.openEditSlideOut(this.updTask, event);
    }
  }

  cancelLongPress() {
    if (this.longPressTimeout) {
      clearTimeout(this.longPressTimeout);
    }
    this.isLongPress = false;
  }

  private loadInitialData(): void {
    if (
      this.user &&
      (this.user.roles.includes('am') ||
       this.user.roles.includes('vendor') ||
       this.user.roles.includes('tech'))
    ) {
      return; // Prevent data fetching for AM, Vendor, and Tech roles
    }
    this.isLoading = true;
    
    // Create observables for each data fetch
    const taskCountsObservable = new Observable<TaskCounts>(observer => {
        this.taskService.getTaskCounts();
        this.totalTasksRead$.pipe(take(1)).subscribe({
            next: (counts: TaskCounts) => {
                observer.next(counts);
                observer.complete();
            },
            error: (err) => observer.error(err)
        });
    });

    const tenantContext = this.getTenantContext();
    if (tenantContext) {
      this.reloadDistributionGroupsFromManager();
    }

    const payload: InitialDataPayload = {
        user: this.userService.getUser(),
        requests: this.requestService.getRequests('admin'),
        employees: this.userService.getUsersByTenant(tenantContext),
        taskCounts: taskCountsObservable,
        pendingVendors: this.tempUserService.getPendingVendors(tenantContext)
    };

    forkJoin(payload).pipe(
        tap((response: InitialDataResponse) => {
            this.usr = response.user;
            this.requests = response.requests.sort((a, b) => a.name.localeCompare(b.name));
            this.tenantUsers = response.employees
                .filter(user => !user.roles.some(role => role.name === 'developer'))
                .sort((a, b) => a.name.localeCompare(b.name));
            this.pendingVendorApprovalsCount = response.pendingVendors.length;
            this.filterOptions();
            this.fetchTasks(); // Initial task fetch after data is loaded
        }),
        finalize(() => {
            this.isLoading = false;
            this.cdr.markForCheck();
        })
    ).subscribe({
        error: (error) => {
            console.error('Error loading initial data:', error);
            this.isLoading = false;
            this.cdr.markForCheck();
        }
    });
  }

  private socketRefreshDebounceInitialized = false;

  private setupSocketRefreshDebounce(): void {
    if (this.socketRefreshDebounceInitialized) {
      return;
    }
    this.socketRefreshDebounceInitialized = true;
    this.socketUpdateSubject
      .pipe(debounceTime(DASHBOARD_SOCKET_DEBOUNCE_MS), takeUntil(this.destroy$))
      .subscribe((payload: TaskUpdatePayload) => {
        this.refreshFromSocket(payload);
      });
  }

  private setupDashboardSocketRefresh(): void {
    this.liveSync.bindTaskRefreshSocket(
      this.ws,
      this.dashboardSocketHandlers,
      this.socketBindAttempts,
      () => this.resolveDashboardTenant(),
      (payload) => this.socketUpdateSubject.next(payload as TaskUpdatePayload),
      15,
      () => this.getViewerRoleNames()
    );
  }

  private getViewerRoleNames(): string[] {
    const roles = this.user?.roles ?? [];
    return roles.map((role: string | { name?: string }) =>
      typeof role === 'string' ? role : role?.name ?? ''
    ).filter(Boolean);
  }

  private setupAdaptivePolling(): void {
    if (this.pollingTickInterval) {
      clearInterval(this.pollingTickInterval);
    }
    this.pollingTickInterval = setInterval(() => {
      if (document.hidden || this.isEditMode) {
        return;
      }
      const shouldPoll = this.liveSync.shouldRunSafetyPoll(
        this.isWebSocketConnected,
        this.liveSync.lastSocketRefresh
      );
      if (!shouldPoll) {
        return;
      }
      if (this.isWebSocketConnected) {
        this.runCountsRefresh();
      } else {
        this.runLightRefresh(true);
      }
    }, DASHBOARD_POLLING_WS_DISCONNECTED_MS);
  }

  @HostListener('document:visibilitychange')
  onDocumentVisibilityChange(): void {
    if (document.visibilityState !== 'visible') {
      return;
    }
    if (!this.ws.isConnected()) {
      this.ws.connect();
      this.setupDashboardSocketRefresh();
    }
    if (!this.isEditMode) {
      this.runLightRefresh(true);
    }
  }

  // Add a new method for filter changes
  private changeFilter(newFilter: string) {
    this.currentPage = 1; // Reset to first page only when changing filters
    this.activeFilter = newFilter;
    this.fetchTasks();
  }

  acknowledgeCloseCSRTask(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.showCloseCSRTaskModal = true;
    this.cdr.detectChanges();
  }

  closeCSRTaskModal() {
    this.showCloseCSRTaskModal = false;
    this.cdr.detectChanges();
  }

  closeCSRTask(task: Task): void {
    this.showCloseCSRTaskModal = false;
    this.taskService.csrCloseTask(task._id!, { closedBy: 'csr' }).subscribe({
      next: () => {
        this.sysNotifyService.showSuccess('Request closed successfully', 3000, 'right', 'top');
        this.toggleSlideOut();
        this.cdr.markForCheck();
        this.updateView();
        this.taskService.getTaskCounts();
      },
      error: (error) => {
        console.error('Error closing task:', error);
        this.sysNotifyService.showError('Error closing task', 3000, 'right', 'top');
        this.cdr.markForCheck();
      }
    });
  }

  acknowledgeClosePorterTask(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    this.showClosePorterTaskModal = true;
    this.cdr.detectChanges();
  }

  closePorterTaskModal() {
    this.showClosePorterTaskModal = false;
    this.cdr.detectChanges();
  }

  closePorterTask(task: Task): void {
    this.showClosePorterTaskModal = false;
    this.taskService.porterCloseTask(task._id!, { closedBy: 'porter' }).subscribe({
      next: () => {
        this.sysNotifyService.showSuccess('Request closed successfully', 3000, 'right', 'top');
        this.toggleSlideOut();
        this.cdr.markForCheck();
        this.updateView();
        this.taskService.getTaskCounts();
      },
      error: (error) => {
        console.error('Error closing task:', error);
        this.sysNotifyService.showError('Error closing task', 3000, 'right', 'top');
        this.cdr.markForCheck();
      }
    });
  }

  getTechRequestsCount(): number {
    if (this.user?.roles?.includes('csr')) {
      return this.csrOpenTasksCount_cardCount || 0;
    } else if (this.user?.roles?.includes('porter')) {
      return this.porterPending_cardCount || 0;
    }
    return this.myTechRequests_cardCount || 0;
  }

  getTechRequestsReadCount(): number {
    if (this.user?.roles?.includes('csr')) {
      return this.csrOpenTasksCount || 0;
    } else if (this.user?.roles?.includes('porter')) {
      return this.porterPending || 0;
    }
    return this.newTechRequestsCount || 0;
  }

  // Add handler method for request type change
  onRequestTypeChange() {
    this.isFilterLoading = true;
    if (this.selectedRequestType && !this.availableRequestTypes.some(req => req._id === this.selectedRequestType)) {
      this.selectedRequestType = '';
      this.selectedRequestName = 'All Request Types';
    } else if (this.selectedRequestType) {
      const selectedRequest = this.availableRequestTypes.find(req => req._id === this.selectedRequestType);
      this.selectedRequestName = selectedRequest ? selectedRequest.name : 'All Request Types';
    } else {
      this.selectedRequestName = 'All Request Types';
    }
    this.currentPage = 1;
    this.filterChangeSubject.next();
    setTimeout(() => {
      this.isFilterLoading = false;
      this.cdr.markForCheck();
    }, 300);
  }

  // Add method to get unique request types from current tasks
  private updateAvailableRequestTypes(tasks: Task[]) {
    const uniqueRequests = new Map();
    const counts: { [key: string]: number } = {};
    
    tasks.forEach(task => {
      if (task.request && task.request._id) {
        uniqueRequests.set(task.request._id, task.request);
        counts[task.request._id] = (counts[task.request._id] || 0) + 1;
      }
    });
    
    this.availableRequestTypes = Array.from(uniqueRequests.values());
    this.requestTypeCounts = counts;
  }

  // Add method to get count for a request type
  getRequestTypeCount(requestId: string): number {
    return this.requestTypeCounts[requestId] || 0;
  }

  // Add method to clear request type filter
  clearRequestTypeFilter() {
    this.isFilterLoading = true;
    this.selectedRequestType = '';
    this.selectedRequestName = 'All Request Types';
    this.currentPage = 1;
    this.filterChangeSubject.next();
    setTimeout(() => {
      this.isFilterLoading = false;
      this.cdr.markForCheck();
    }, 300);
  }

  // Add keyboard navigation support
  @HostListener('keydown', ['$event'])
  handleKeyboardNavigation(event: KeyboardEvent) {
    if (event.target instanceof HTMLSelectElement) {
      if (event.key === 'Escape') {
        this.clearRequestTypeFilter();
      }
    }
  }

  /**
   * Media without subletId (mobile "General Photos" / uploadTaskMedia). Sublet shots are shown under sublets.
   */
  getTaskLevelMediaForStrip(task: Task | null | undefined): NonNullable<Task['media']> {
    if (!task?.media?.length) return [];
    return task.media.filter((m) => !m?.subletId).slice(0, 3);
  }

  refreshTaskLevelMediaStrip(): void {
    this.taskLevelMediaStrip = [...this.getTaskLevelMediaForStrip(this.updTask)];
  }

  getMediaImageUrl(media: MediaItem): string {
    const id = media.mediaId?._id;
    const url = media.mediaId?.imageUrl;
    if (!url) {
      return '';
    }
    return id ? `${url}${url.includes('?') ? '&' : '?'}v=${id}` : url;
  }

  trackByMediaId(_index: number, media: MediaItem): string {
    return media.mediaId?._id || `media-${_index}`;
  }

  openImageViewerTaskLevel(index: number) {
    const strip = this.taskLevelMediaStrip;
    if (strip.length > 0) {
      this.imageViewerService.open(strip.map((m) => this.getMediaImageUrl(m)), index);
    }
  }

  showImageMediaUpload(requestId: any): boolean {
    if (!requestId || requestId === '') {
      return false;
    }
    const matchedRequest = this.requests.find(req => req._id === requestId);
    if (!matchedRequest) {
      return false;
    }
    const requestName = matchedRequest.name;
    return requestName === "In Process Photos" || requestName === "Phase Change";
  }

  promptDeleteImage(media: any, event: Event) {
    event.stopPropagation();
    event.preventDefault();
    console.log('Prompting delete for', media);
    this.imageToDelete = media;
    setTimeout(() => {
      this.showDeleteImageModal = true;
      this.cdr.detectChanges();
    }, 100);
  }

  closeDeleteImageModal() {
    this.showDeleteImageModal = false;
    this.cdr.detectChanges();
  }

  deleteImageFromTask() {
    const media = this.imageToDelete;
    this.showDeleteImageModal = false;
    if (media && this.updTask && this.updTask._id && media.mediaId?._id) {
      this.taskService.deleteTaskMedia(this.updTask._id, media.mediaId._id).subscribe({
        next: (mediaResp: any) => {
          if (this.updTask.media) {
            this.updTask.media = [...mediaResp.task.media];
            this.cachedMedia = [...mediaResp.task.media];
            this.refreshTaskLevelMediaStrip();
          }
          this.cdr.markForCheck();
          this.sysNotifyService.showSuccess('Image deleted successfully', 2000, 'right', 'top');
        },
        error: () => {
          this.sysNotifyService.showError('Error deleting image', 2000, 'right', 'top');
        }
      });
    }
    this.imageToDelete = null;
  }

  // Helper to check if request is empty (for edit form dropdown)
  isEmptyRequest(val: any): boolean {
    return !val || (typeof val === 'string' && val === '');
  }

  // Helper to check if employee is empty (for edit form dropdown)
  isEmptyEmployee(val: any): boolean {
    return !val || (typeof val === 'string' && val === '');
  }

  // trackBy function for requests
  trackByRequestId(index: number, req: any) {
    return req._id;
  }

  private cachedMedia: any[] = [];
  taskLevelMediaStrip: MediaItem[] = [];

  private socketUpdateSubject = new Subject<TaskUpdatePayload>();

  private filterChangeSubject = new Subject<void>();

  private updateTaskInCache(updatedTask: Task) {
    const cacheKey = this.activeFilter;
    const page = this.currentPage;
    if (this.taskCache[cacheKey]?.[page]) {
      const idx = this.taskCache[cacheKey][page].findIndex(t => t._id === updatedTask._id);
      if (idx !== -1) {
        this.taskCache[cacheKey][page][idx] = { ...updatedTask };
        this.processTasks(this.taskCache[cacheKey][page]);
      }
    }
  }

  private updateTaskReadStatusInCache(taskId: string) {
    const cacheKey = this.activeFilter;
    const page = this.currentPage;
    if (this.taskCache[cacheKey]?.[page]) {
      const idx = this.taskCache[cacheKey][page].findIndex(t => t._id === taskId);
      if (idx !== -1) {
        this.taskCache[cacheKey][page][idx] = {
          ...this.taskCache[cacheKey][page][idx],
          isRead: true
        };
        this.processTasks(this.taskCache[cacheKey][page]);
      }
    }
  }

  openBlueprintProgressModal(task: Task) {
    this.taskService.getBlueprintProgress(task._id as string).subscribe(progress => {
      console.log('progressV2', progress);
      const modalRef = this.modalService.open(BlueprintProgressModalComponent, {
        data: progress.progress
      });

      // Subscribe to isLoggedIn$ and close modal if user logs out
      this.authService.isLoggedIn$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoggedIn => {
        if (!isLoggedIn) {
          modalRef.close();
        }
      });

    });
  }

  // Add this helper method to the class
  private shouldFetchTaskCounts(): boolean {
    return this.user &&
      !this.user.roles.includes('tech') &&
      !this.user.roles.includes('vendor') &&
      !this.user.roles.includes('am');
  }

  get sortedUpdTaskComments() {
    if (!this.updTask || !this.updTask.commentMsg) return [];
    // Oldest first so the thread reads top-to-bottom into the latest reply.
    return [...this.updTask.commentMsg].sort(
      (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    );
  }



}

// Type guard for request with _id
function isRequestWithId(obj: any): obj is { _id: string } {
  return obj && typeof obj === 'object' && typeof obj._id === 'string';
}

